// The SVG icon set painted by CSS masks (promoted from the UI prototype, frontend/src/prototype).
// One style for every glyph: viewBox 0 0 24 24, artwork in the 20 x 20 inner grid, stroke 1.75
// round caps / joins, rect corners rx 2, circles r 9; filled glyphs (pause, play, star,
// trophy-solid, dot) use fill. The colour in the SVG does not matter: the glyphs are used as
// `mask-image`, the CSS `background` paints them. `injectIcons()` emits every icon as
// `--i-<name>: url("data:image/svg+xml,...")` on :root in one <style>, so a shell glyph is CSS only:
//   a.menu_toggle_sound::before { content: ''; width: 18px; height: 18px; background: currentColor;
//     mask: var(--i-sound-on) center / contain no-repeat; }
// Two-layer marks (an accent box with a white tick) layer `--i-check` or `--i-dot` over a CSS fill.
const STROKE = `fill="none" stroke="#000" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"`;
const svg = (body: string, attrs = STROKE) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${attrs}>${body}</svg>`;
const FILL = `fill="#000" stroke="none"`;
/** The 5-point star, r 9 / 4, point up. */
const STAR = 'M12 3l2.35 5.76 6.21.46-4.76 4.02 1.49 6.04L12 16l-5.29 3.28 1.49-6.04-4.76-4.02 6.21-.46z';

const DRAWINGS: Record<string, string> = {
  // bars
  menu: svg('<path d="M5 7h14M5 12h14M5 17h14"/>'),
  // an open door: the Menu button during an online game (it leaves the game; owner 2026-10-29: "ikona drzwi, a nie menu")
  door: svg('<path d="M13 4h3a2 2 0 0 1 2 2v14"/><path d="M3 20h3M13 20h8"/><path d="M10 12v.01"/><path d="M13 4.56v16.16a1 1 0 0 1-1.24.97L5 20V5.56a2 2 0 0 1 1.52-1.94l4-1A2 2 0 0 1 13 4.56z"/>'),
  'chevron-down': svg('<path d="M7 10l5 5 5-5"/>'),
  'chevron-left': svg('<path d="M14.5 6l-6 6 6 6"/>'),
  'chevron-right': svg('<path d="M9.5 6l6 6-6 6"/>'),
  x: svg('<path d="M7 7l10 10M17 7L7 17"/>'),
  minus: svg('<path d="M7 12h10"/>'),
  undo: svg('<path d="M9 14L4 9l5-5"/><path d="M4 9h10a5.5 5.5 0 0 1 0 11h-4"/>'),
  help: svg(
    '<circle cx="12" cy="12" r="9"/><path d="M9.6 9.6a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1.1.9-1.1 1.6"/><path d="M12 17h.01"/>',
  ),
  info: svg('<circle cx="12" cy="12" r="9"/><path d="M12 8h.01M12 11v5"/>'),
  shield: svg('<path d="M12 3l7 3v5.5c0 4.4-3 8-7 9.5-4-1.5-7-5.1-7-9.5V6z"/><path d="M9 12l2 2 4-4"/>'),
  mail: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3.5 6.5l8.5 6.5 8.5-6.5"/>'),
  // Mahjong's hint (a bulb)
  bulb: svg('<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.6 10.8c.6.5 1.1 1.3 1.1 2.2h5c0-.9.5-1.7 1.1-2.2A6 6 0 0 0 12 3z"/>'),
  book: svg(
    '<path d="M12 7.5c-1.6-1.6-4-2.2-7-2.2v12.4c3 0 5.4.6 7 2.2 1.6-1.6 4-2.2 7-2.2V5.3c-3 0-5.4.6-7 2.2z"/><path d="M12 7.5v12.4"/>',
  ),
  user: svg('<circle cx="12" cy="8.5" r="3.5"/><path d="M5 20a7 7 0 0 1 14 0"/>'),
  'user-circle': svg(
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="10" r="3"/><path d="M6.5 18.5a6 6 0 0 1 11 0"/>',
  ),
  rank: svg('<path d="M4 19v-9h5v9M9.5 19V5h5v14M15 19v-7h5v7"/><path d="M3 19h18"/>'),
  trophy: svg(
    '<path d="M8 4h8v5a4 4 0 0 1-8 0V4z"/><path d="M8 6H5.5v1.5A3 3 0 0 0 8 10.5M16 6h2.5v1.5a3 3 0 0 1-2.5 3"/><path d="M12 13v3.5M8.5 20h7M9.5 16.5h5v3.5"/>',
  ),
  'trophy-solid': svg(
    '<path d="M7 3h10v6a5 5 0 0 1-10 0z"/><rect x="10.75" y="13" width="2.5" height="4" rx=".5"/><path d="M8 17h8a1 1 0 0 1 1 1v3H7v-3a1 1 0 0 1 1-1z"/><path d="M7 5H4v2a4 4 0 0 0 4 4V9a2 2 0 0 1-2-2V5zM17 5h3v2a4 4 0 0 1-4 4V9a2 2 0 0 0 2-2V5z"/>',
    FILL,
  ),
  clock: svg('<circle cx="12" cy="12" r="8"/><path d="M12 7.5V12h3.5"/>'),
  // the lobby's Tables tab (owner's pick 2026-10-31): a table that waits, a taken seat (a token), a free seat (a dotted ring)
  // a robot's head: the badge of a seat a bot plays for its player (online/SeatAvatar.tsx; owner 2026-11-02)
  bot: svg('<rect x="5" y="8" width="14" height="11" rx="3"/><path d="M12 8V5M9.5 13v.5M14.5 13v.5M9.5 16.5h5"/><circle cx="12" cy="4" r="1"/>'),
  seat: svg('<path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 6.2a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6zM6.2 18.2c.8-2.8 3.2-4.2 5.8-4.2s5 1.4 5.8 4.2a8.2 8.2 0 0 1-11.6 0z"/>', 'fill="#000" fill-rule="evenodd"'),
  'seat-free': svg('<circle cx="12" cy="12" r="9"/>', 'fill="none" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-dasharray="0.1 4.8"'),
  'star-small': svg(`<path d="${STAR}"/>`, FILL),
  moves: svg('<path d="M4 8h14M15 5l3 3-3 3"/><path d="M20 16H6M9 13l-3 3 3 3"/>'),
  // menu entries
  'new-game': svg('<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M12 9v6M9 12h6"/>'),
  restart: svg('<path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M17 2.5V7h-4.5"/>'),
  games: svg(
    '<rect x="4" y="4" width="7" height="7" rx="2"/><rect x="13" y="4" width="7" height="7" rx="2"/><rect x="4" y="13" width="7" height="7" rx="2"/><rect x="13" y="13" width="7" height="7" rx="2"/>',
  ),
  multiplayer: svg(
    '<circle cx="9" cy="8.5" r="3.5"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6"/><path d="M21 20a6 6 0 0 0-4.5-5.6"/>',
  ),
  options: svg(
    '<path d="M4 7h3M11 7h9M4 12h9M17 12h3M4 17h5M13 17h7"/><circle cx="9" cy="7" r="2"/><circle cx="15" cy="12" r="2"/><circle cx="11" cy="17" r="2"/>',
  ),
  theme: svg(
    '<circle cx="12" cy="12" r="9"/><path d="M8.5 10.5h.01M12 7.5h.01M15.5 10.5h.01" stroke-width="2.5"/><circle cx="13.5" cy="15.5" r="1.75"/>',
  ),
  logout: svg('<path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4"/><path d="M15 8l4 4-4 4M19 12H9"/>'),
  'plus-circle': svg('<circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/>'),
  // footer
  'sound-on': svg('<path d="M5 10h4l4-4v12l-4-4H5z"/><path d="M16.5 9a4.5 4.5 0 0 1 0 6"/>'),
  'sound-off': svg('<path d="M5 10h4l4-4v12l-4-4H5z"/><path d="M16 9.5l4 5M20 9.5l-4 5"/>'),
  pause: svg('<rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/>', FILL),
  play: svg('<path d="M7 5l12 7-12 7z"/>', 'fill="#000" stroke="#000" stroke-width="1.5" stroke-linejoin="round"'),
  cards: svg(
    '<path d="M9 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><rect x="9" y="4" width="11" height="15" rx="2"/><path d="M12.5 7.5h.01" stroke-width="2.5"/>',
  ),
  'fullscreen-enter': svg('<path d="M4 10V4h6M14 4h6v6M20 14v6h-6M10 20H4v-6"/>'),
  'fullscreen-exit': svg('<path d="M10 4v6H4M20 10h-6V4M14 20v-6h6M4 14h6v6"/>'),
  'chevrons-down': svg('<path d="M7 6.5l5 5 5-5M7 12.5l5 5 5-5"/>'),
  // marks
  'radio-off': svg('<circle cx="12" cy="12" r="9"/>'),
  'radio-on': svg('<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5" fill="#000"/>'),
  dot: svg('<circle cx="12" cy="12" r="4.5"/>', FILL),
  'check-off': svg('<rect x="4" y="4" width="16" height="16" rx="3"/>'),
  'check-on': svg('<rect x="4" y="4" width="16" height="16" rx="3"/><path d="M8 12l3 3 5-6"/>'),
  check: svg('<path d="M6 12.5l4 4 8-9" stroke-width="2.5"/>'),
  star: svg(`<path d="${STAR}"/>`, FILL),
  'star-outline': svg(`<path d="${STAR}"/>`),
};

/** Names the plan uses in more than one spelling: the same drawing under both. */
const ALIASES: Record<string, string> = {
  'help-circle': 'help',
  'rotate-ccw': 'restart',
  grid: 'games',
  users: 'multiplayer',
  sliders: 'options',
  palette: 'theme',
  'volume-off': 'sound-off',
  collapse: 'fullscreen-exit',
  close: 'x',
};

export const ICONS: Record<string, string> = {
  ...DRAWINGS,
  ...Object.fromEntries(Object.entries(ALIASES).map(([alias, name]) => [alias, DRAWINGS[name]!])),
};
export const ICON_NAMES = Object.keys(ICONS);

/** `url("data:image/svg+xml,...")` for one SVG string (only the characters CSS / URLs trip over are encoded). */
export const svgUrl = (markup: string) =>
  `url("data:image/svg+xml,${markup.replace(/"/g, "'").replace(/[#<>%{}]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)}")`;
export const iconUrl = (name: string) => svgUrl(ICONS[name] ?? '');

const STYLE_ID = 'app-icons';

/** One <style id="app-icons"> with every icon as a custom property on :root. Idempotent. */
export function injectIcons() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `:root{${Object.keys(ICONS)
    .map((n) => `--i-${n}:${iconUrl(n)};`)
    .join('')}}`;
  document.head.appendChild(style);
}
